import apiService from './api.service';
import { baixarArquivo, lerBlob, nomeDoContentDisposition } from '@/utils/arquivo';
import type {
  SeopAllocationOverride,
  SeopAllocationOverrideInput,
  SeopAutoBalanceResult,
  SeopBands,
  SeopCalc,
  SeopCapacity,
  SeopCapacityInput,
  SeopDemand,
  SeopForecastCommitPayload,
  SeopForecastImport,
  SeopLever,
  SeopLotParam,
  SeopMonth,
  SeopPhysicalResource,
  SeopPlanSetting,
  SeopProduct,
  SeopProductionAnalysis,
  SeopProductDetail,
  SeopRenameImpact,
  SeopResource,
  SeopRestoreResult,
  SeopScenario,
} from '@/types/seop';

/** As listas do módulo não são paginadas hoje, mas o DRF pode passar a paginar. */
type Paginated<T> = T[] | { results?: T[] };

function unwrapList<T>(data: Paginated<T>): T[] {
  return Array.isArray(data) ? data : data?.results ?? [];
}

/**
 * Reconstitui o erro do backend quando ele veio como `Blob` e roteia o gate.
 *
 * Sem isto, o 403 da feature desligada morre em silêncio no download.
 */
async function erroDeBlob(erro: unknown): Promise<unknown> {
  const resposta = (erro as { response?: { status?: number; data?: unknown } })?.response;
  if (!(resposta?.data instanceof Blob)) return erro;
  let corpo: Record<string, unknown> = {};
  try {
    corpo = JSON.parse(await lerBlob(resposta.data));
  } catch {
    corpo = {};
  }
  if (resposta.status === 403 && corpo.error === 'seop_feature_not_enabled') {
    window.dispatchEvent(new CustomEvent('seop-feature-blocked', { detail: corpo }));
  }
  return { ...(erro as object), response: { ...resposta, data: corpo } };
}

/** O nome vem do `Content-Disposition`; o fallback evita `download` vazio. */
function nomeDoArquivo(headers: unknown, month: number): string {
  return nomeDoContentDisposition(headers, `report_cenarios_M${month + 1}.xlsx`);
}

/**
 * Cliente de `/api/seop/*` (B03).
 *
 * Tudo passa pelo `apiService`, que já injeta o JWT, faz refresh em 401 e roteia
 * 402/403 para os hosts de toast/modal — inclusive o 403 de
 * `seop_feature_not_enabled`, que o `SeopFeatureBlockedHost` escuta.
 */
class SeopService {
  async getMonths(): Promise<SeopMonth[]> {
    const response = await apiService.get<Paginated<SeopMonth>>('/seop/months/');
    return unwrapList(response.data);
  }

  async getPlanSettings(): Promise<SeopPlanSetting[]> {
    const response = await apiService.get<Paginated<SeopPlanSetting>>('/seop/plan-settings/');
    return unwrapList(response.data);
  }

  /** Nome do cenário vivo, exibido no cabeçalho do módulo. Vazio se não houver. */
  async getCurrentScenarioName(): Promise<string> {
    const settings = await this.getPlanSettings();
    const linha = settings.find((x) => x.key === 'current_scenario_name');
    return typeof linha?.value === 'string' ? linha.value : '';
  }

  async getProducts(): Promise<SeopProduct[]> {
    const response = await apiService.get<Paginated<SeopProduct>>('/seop/products/');
    return unwrapList(response.data);
  }

  /** Recalcula sempre no servidor — nada de cálculo é persistido. */
  async getThresholds(): Promise<SeopBands> {
    const { data } = await apiService.get<SeopBands>('/seop/thresholds/');
    return data;
  }

  async updateThresholds(payload: Partial<SeopBands>): Promise<SeopBands> {
    const { data } = await apiService.patch<SeopBands>('/seop/thresholds/', payload);
    return data;
  }

  async getCalc(month: number): Promise<SeopCalc> {
    const response = await apiService.get<SeopCalc>(`/seop/calc/?month=${month}`);
    return response.data;
  }

  async getAllocationOverrides(monthId: string): Promise<SeopAllocationOverride[]> {
    const response = await apiService.get<Paginated<SeopAllocationOverride>>(
      `/seop/allocation-overrides/?month=${monthId}`,
    );
    return unwrapList(response.data);
  }

  /**
   * Grava a alocação de uma etapa. É um upsert por (produto, etapa, mês) — o
   * backend usa POST com semântica de PUT, então regravar não dá 400.
   */
  async putAllocationOverride(payload: SeopAllocationOverrideInput): Promise<SeopAllocationOverride> {
    const response = await apiService.post<SeopAllocationOverride>('/seop/allocation-overrides/', payload);
    return response.data;
  }

  // ---------------------------------------------------------------- cadastros

  async getResources(): Promise<SeopResource[]> {
    return unwrapList((await apiService.get<Paginated<SeopResource>>('/seop/resources/')).data);
  }

  async createResource(payload: Partial<SeopResource>): Promise<SeopResource> {
    return (await apiService.post<SeopResource>('/seop/resources/', payload)).data;
  }

  async updateResource(id: string, payload: Partial<SeopResource>): Promise<SeopResource> {
    return (await apiService.patch<SeopResource>(`/seop/resources/${id}/`, payload)).data;
  }

  async deleteResource(id: string): Promise<void> {
    await apiService.delete(`/seop/resources/${id}/`);
  }

  /**
   * Quantas linhas um rename arrastaria. As referências a recurso são por NOME, não FK:
   * renomear alcança rotas, lotes, alavancas, custos, alocações e cenários salvos, e é
   * isso que a tela mostra antes de confirmar.
   */
  async getRenameImpact(id: string): Promise<SeopRenameImpact> {
    return (await apiService.get<SeopRenameImpact>(`/seop/resources/${id}/rename-impact/`)).data;
  }

  async getProductsDetail(): Promise<SeopProductDetail[]> {
    return unwrapList((await apiService.get<Paginated<SeopProductDetail>>('/seop/products/')).data);
  }

  async createProduct(payload: Partial<SeopProductDetail>): Promise<SeopProductDetail> {
    return (await apiService.post<SeopProductDetail>('/seop/products/', payload)).data;
  }

  async updateProduct(id: string, payload: Partial<SeopProductDetail>): Promise<SeopProductDetail> {
    return (await apiService.patch<SeopProductDetail>(`/seop/products/${id}/`, payload)).data;
  }

  async deleteProduct(id: string): Promise<void> {
    await apiService.delete(`/seop/products/${id}/`);
  }

  async getLotParams(): Promise<SeopLotParam[]> {
    return unwrapList((await apiService.get<Paginated<SeopLotParam>>('/seop/lot-params/')).data);
  }

  async createLotParam(payload: Partial<SeopLotParam>): Promise<SeopLotParam> {
    return (await apiService.post<SeopLotParam>('/seop/lot-params/', payload)).data;
  }

  async updateLotParam(id: string, payload: Partial<SeopLotParam>): Promise<SeopLotParam> {
    return (await apiService.patch<SeopLotParam>(`/seop/lot-params/${id}/`, payload)).data;
  }

  async deleteLotParam(id: string): Promise<void> {
    await apiService.delete(`/seop/lot-params/${id}/`);
  }

  async getPhysicalResources(): Promise<SeopPhysicalResource[]> {
    return unwrapList(
      (await apiService.get<Paginated<SeopPhysicalResource>>('/seop/physical-resources/')).data,
    );
  }

  async createPhysicalResource(p: Partial<SeopPhysicalResource>): Promise<SeopPhysicalResource> {
    return (await apiService.post<SeopPhysicalResource>('/seop/physical-resources/', p)).data;
  }

  async updatePhysicalResource(
    id: string,
    p: Partial<SeopPhysicalResource>,
  ): Promise<SeopPhysicalResource> {
    return (await apiService.patch<SeopPhysicalResource>(`/seop/physical-resources/${id}/`, p)).data;
  }

  async deletePhysicalResource(id: string): Promise<void> {
    await apiService.delete(`/seop/physical-resources/${id}/`);
  }

  async getLevers(): Promise<SeopLever[]> {
    return unwrapList((await apiService.get<Paginated<SeopLever>>('/seop/levers/')).data);
  }

  // ---------------------------------------------------------------- demanda

  async getDemands(monthId?: string): Promise<SeopDemand[]> {
    const qs = monthId ? `?month=${monthId}` : '';
    return unwrapList((await apiService.get<Paginated<SeopDemand>>(`/seop/demand/${qs}`)).data);
  }

  /** PATCH em lote — a tela acumula as células editadas e manda de uma vez. */
  async patchDemandBatch(linhas: Array<{ product: string; month: string; quantity: number }>): Promise<void> {
    await apiService.patch('/seop/demand/batch/', linhas);
  }

  // ---------------------------------------------------------------- capacidade

  async getCapacities(monthId?: string): Promise<SeopCapacity[]> {
    const qs = monthId ? `?month=${monthId}` : '';
    return unwrapList((await apiService.get<Paginated<SeopCapacity>>(`/seop/capacity/${qs}`)).data);
  }

  async patchCapacityBatch(linhas: SeopCapacityInput[]): Promise<SeopCapacity[]> {
    return (await apiService.patch<SeopCapacity[]>('/seop/capacity/batch/', linhas)).data;
  }

  async createMonth(payload: { date: string; index: number; label: string }): Promise<SeopMonth> {
    return (await apiService.post<SeopMonth>('/seop/months/', payload)).data;
  }

  /** Cria o mês `AAAA-MM` em qualquer ponto do horizonte; capacidade e mix vêm do mês vizinho. */
  async addMonth(date: string): Promise<SeopMonth> {
    return (await apiService.post<SeopMonth>('/seop/months/add/', { date })).data;
  }

  /** Corrige o mês de calendário: o que foi lançado nele e os cenários salvos acompanham. */
  async redateMonth(id: string, date: string): Promise<SeopMonth> {
    return (await apiService.post<SeopMonth>(`/seop/months/${id}/redate/`, { date })).data;
  }

  // ---------------------------------------------------------------- cenários

  /** Plano × produção real do período. Bate na réplica do Datasul — pode responder 503. */
  async getProductionAnalysis(month: number): Promise<SeopProductionAnalysis> {
    return (
      await apiService.get<SeopProductionAnalysis>(`/seop/production-analysis/?month=${month}`)
    ).data;
  }

  async getScenarios(): Promise<SeopScenario[]> {
    return unwrapList((await apiService.get<Paginated<SeopScenario>>('/seop/scenarios/')).data);
  }

  async createScenario(name: string, month: number): Promise<SeopScenario> {
    return (await apiService.post<SeopScenario>('/seop/scenarios/', { name, month })).data;
  }

  async deleteScenario(id: string): Promise<void> {
    await apiService.delete(`/seop/scenarios/${id}/`);
  }

  /** Corrige o mês de um cenário salvo no mês errado. Os números do cenário e o plano atual não mudam. */
  async redateScenario(id: string, date: string): Promise<SeopScenario> {
    return (await apiService.post<SeopScenario>(`/seop/scenarios/${id}/redate/`, { date })).data;
  }

  /** Roda o motor sobre o snapshot, **sem tocar no estado vivo**. */
  async getCalcFromScenario(id: string, month: number): Promise<SeopCalc> {
    return (await apiService.get<SeopCalc>(`/seop/calc-from-scenario/?id=${id}&month=${month}`)).data;
  }

  /**
   * Torna o cenário salvo o plano atual DO MÊS DELE (voltam as decisões do plano; o cadastro
   * e os outros meses ficam).
   * O backend cria um snapshot do plano atual ANTES de aplicar — nunca é destrutivo.
   * `backupName` dá nome a esse snapshot; sem nome, é o "Backup automático — …".
   */
  async restoreScenario(id: string, backupName?: string): Promise<SeopRestoreResult> {
    const corpo = backupName?.trim() ? { confirm: true, backup_name: backupName.trim() } : { confirm: true };
    return (await apiService.post<SeopRestoreResult>(`/seop/scenarios/${id}/restore/`, corpo)).data;
  }

  // ------------------------------------------------------ import do forecast (B06)

  /**
   * Sobe o xlsx e recebe o PREVIEW — nada é gravado aqui (B-D7). O upload usa
   * `uploadFile` do `apiService`, que já monta o multipart e injeta o JWT.
   */
  async uploadForecast(file: File): Promise<SeopForecastImport> {
    return (await apiService.uploadFile<SeopForecastImport>('/seop/forecast-imports/', file)).data;
  }

  async listImports(): Promise<SeopForecastImport[]> {
    return unwrapList((await apiService.get<Paginated<SeopForecastImport>>('/seop/forecast-imports/')).data);
  }

  /** Reabre um preview salvo — é o que deixa fechar a aba e voltar depois. */
  async getImport(id: string): Promise<SeopForecastImport> {
    return (await apiService.get<SeopForecastImport>(`/seop/forecast-imports/${id}/`)).data;
  }

  /** Aplica a conciliação. Idempotente por produto×mês no backend. */
  async commitImport(id: string, payload: SeopForecastCommitPayload): Promise<SeopForecastImport> {
    return (await apiService.post<SeopForecastImport>(`/seop/forecast-imports/${id}/commit/`, payload)).data;
  }

  /** Descarta sem apagar: import errado explica um buraco no histórico. */
  async discardImport(id: string): Promise<void> {
    await apiService.delete(`/seop/forecast-imports/${id}/`);
  }

  // ------------------------------------------------------ export do report (B07)

  /**
   * Baixa o Report de Cenários (.xlsx) e dispara o download no browser.
   *
   * **O erro chega como `Blob`, não como objeto** (mesma classe do DT-12): com
   * `responseType: 'blob'` o axios repassa `xhr.response` cru, inclusive na
   * resposta de erro, então o `if` do interceptor que compara
   * `data?.error === 'seop_feature_not_enabled'` não casa e o
   * `SeopFeatureBlockedHost` nunca dispara — o usuário clicaria em Exportar e
   * nada aconteceria. Por isso o 403 é roteado aqui, à mão.
   */
  async exportReport(ids: string[], month: number): Promise<void> {
    try {
      const resposta = await apiService.get<Blob>(
        `/seop/scenarios/export/?ids=${ids.join(',')}&month=${month}`,
        { responseType: 'blob' },
      );
      baixarArquivo(resposta.data, nomeDoArquivo(resposta.headers, month));
    } catch (erro) {
      throw await erroDeBlob(erro);
    }
  }

  async postAutoBalance(month: number): Promise<SeopAutoBalanceResult> {
    const response = await apiService.post<SeopAutoBalanceResult>('/seop/auto-balance/', { month });
    return response.data;
  }
}

export const seopService = new SeopService();
export default seopService;
